/**
 * Time-range model for the detection list's filter bar.
 *
 * Three preset windows ("1h" / "24h" / "7d") plus a "custom" option
 * with explicit since/until. The preset's since/until are computed
 * fresh each time `resolve()` is called so polling against a preset
 * always refreshes against "now"; the custom variant captures Date
 * objects at filter-change time.
 */

export type TimeRangePreset = "1h" | "24h" | "7d";

export type TimeRange =
  | { kind: "preset"; preset: TimeRangePreset }
  | { kind: "custom"; since: Date; until: Date };

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const PRESET_DURATION_MS: Record<TimeRangePreset, number> = {
  "1h": HOUR_MS,
  "24h": 24 * HOUR_MS,
  "7d": 7 * DAY_MS,
};

export function resolveTimeRange(range: TimeRange): {
  since: Date;
  until: Date;
} {
  if (range.kind === "custom") {
    return { since: range.since, until: range.until };
  }
  const now = new Date();
  return {
    since: new Date(now.getTime() - PRESET_DURATION_MS[range.preset]),
    until: now,
  };
}

export const DEFAULT_TIME_RANGE: TimeRange = { kind: "preset", preset: "24h" };
